package features.overtime.handlers

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.HTMLElement
import AppState
import Router
import shared.ui.ModalManager
import features.overtime.data.OvertimeRepository

/**
 * Update the displayed hours when start/end time changes
 */
fun updateOvertimeHours() {
    val startInput = document.getElementById("overtime-start-input") as? HTMLInputElement
    val endInput = document.getElementById("overtime-end-input") as? HTMLInputElement
    val hoursDisplay = document.getElementById("overtime-hours-display") as? HTMLElement
    
    val startTime = startInput?.value ?: ""
    val endTime = endInput?.value ?: ""
    
    if (startTime.isNotEmpty() && endTime.isNotEmpty()) {
        val hours = calculateHoursDisplay(startTime, endTime)
        hoursDisplay?.textContent = hours
    } else {
        hoursDisplay?.textContent = "0.0h"
    }
}

/**
 * Calculate hours between start and end time for display
 */
private fun calculateHoursDisplay(startTime: String, endTime: String): String {
    try {
        val startParts = startTime.split(":")
        val endParts = endTime.split(":")
        
        val startHour = startParts[0].toInt()
        val startMinute = startParts[1].toInt()
        val endHour = endParts[0].toInt()
        val endMinute = endParts[1].toInt()
        
        var totalMinutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute)
        
        // Handle overnight shifts
        if (totalMinutes < 0) {
            totalMinutes += 24 * 60
        }
        
        val hours = totalMinutes / 60.0
        // Use JS toFixed() instead of String.format (not available in Kotlin/JS)
        return "${hours.asDynamic().toFixed(1)}h"
    } catch (e: Throwable) {
        return "0.0h"
    }
}

/**
 * Handler for submitting overtime request
 */
fun submitOvertime() {
    console.log("🔧 submitOvertime() called")
    
    val teamId = AppState.currentTeam?.id
    val userId = AppState.currentUser?.id
    
    if (teamId == null || userId == null) {
        window.alert("No team or user selected")
        return
    }
    
    val dateInput = document.getElementById("overtime-date-input") as? HTMLInputElement
    val startInput = document.getElementById("overtime-start-input") as? HTMLInputElement
    val endInput = document.getElementById("overtime-end-input") as? HTMLInputElement
    val reasonInput = document.getElementById("overtime-reason-input") as? HTMLTextAreaElement
    
    val date = dateInput?.value ?: ""
    val startTime = startInput?.value ?: ""
    val endTime = endInput?.value ?: ""
    val reason = reasonInput?.value?.trim() ?: ""
    
    // Debug logging
    console.log("📋 Form values:")
    console.log("  Date:", date)
    console.log("  Start Time:", startTime)
    console.log("  End Time:", endTime)
    console.log("  Reason:", reason)
    
    // Validation
    if (date.isEmpty()) {
        window.alert("Please select a date")
        return
    }
    
    if (startTime.isEmpty()) {
        window.alert("Please select start time")
        return
    }
    
    if (endTime.isEmpty()) {
        window.alert("Please select end time")
        return
    }
    
    // Validate date is not in the future
    val today = js("new Date().toISOString().split('T')[0]").toString()
    if (date > today) {
        window.alert("Date cannot be in the future")
        return
    }
    
    // Validate end time is after start time (for same day)
    if (startTime >= endTime) {
        // Allow overnight shifts, but warn user
        val confirmed = window.confirm("End time is before start time. This will be treated as an overnight shift. Continue?")
        if (!confirmed) return
    }
    
    // Validate reason length
    if (reason.length < 10) {
        window.alert("Reason must be at least 10 characters (current: ${reason.length})")
        return
    }
    
    console.log("📝 Submitting overtime request:", date, startTime, "to", endTime, "reason:", reason)
    
    // Submit to Firebase
    OvertimeRepository.createOvertimeRequest(teamId, userId, date, startTime, endTime, reason)
        .then { requestId ->
            console.log("✅ Overtime request created:", requestId)
            window.alert("Overtime request submitted successfully!")
            
            // Close modal
            ModalManager.close()
            
            // Refresh the page to show new request
            Router.render()
        }
        .catch { error ->
            console.error("❌ Failed to create overtime request:", error)
            window.alert("Failed to submit overtime request. Please try again.")
        }
}
